import { getWindowSize } from '../window-size-utils';

export enum VIEWABLE_CHANGE_STRATEGY {
  ALWAYS_VISIBLE = 'alwaysVisible',
  BIND_ON_BROWSER_EVENTS = 'bindOnBrowserEvents'
}

export type PolyFillOptions = {
  viewableChangeStrategy: VIEWABLE_CHANGE_STRATEGY;
};

export function getMraidPolyfill(
  options: PolyFillOptions = {
    viewableChangeStrategy: VIEWABLE_CHANGE_STRATEGY.BIND_ON_BROWSER_EVENTS
  }
): Mraid {
  const listeners: AnyObject = {};

  const isViewable = () => {
    const { width, height } = getWindowSize();

    return (
      typeof document.hidden !== 'undefined' &&
      !document.hidden &&
      width > 0 &&
      height > 0
    );
  };

  return {
    __ogyPolyfill: true,
    __ogyViewableChangeStrategy: options.viewableChangeStrategy,
    addEventListener(event: MRAIDEvent | OguryEvent, cb: any) {
      listeners[event] = (listeners[event] || []).concat(cb);

      if (event === 'exposureChange') {
        cb(100);
        return;
      }

      if (
        event === 'viewableChange' &&
        options.viewableChangeStrategy ===
          VIEWABLE_CHANGE_STRATEGY.ALWAYS_VISIBLE
      ) {
        cb(true);
        return;
      }

      if (event === 'viewableChange') {
        const onVisibilityChange = () => {
          const registeredCallbackIndex = listeners[event].indexOf(cb);
          if (registeredCallbackIndex !== -1) {
            const registeredCallback =
              listeners[event][registeredCallbackIndex];
            registeredCallback(isViewable());
          }
        };

        document.addEventListener(
          'visibilitychange',
          onVisibilityChange,
          false
        );
        window.addEventListener('resize', onVisibilityChange, false);
      } else if (event === 'ready') {
        cb();
      }
    },
    createCalendarEvent() {},
    close() {},
    unload,
    expand() {},
    getCurrentPosition() {
      return {
        x: 0,
        y: 0,
        ...getWindowSize()
      };
    },
    getDefaultPosition() {
      return {
        x: 0,
        y: 0,
        ...getWindowSize()
      };
    },
    getExpandProperties() {
      return {
        height: screen.height,
        isModal: true,
        useCustomClose: false,
        width: screen.width
      };
    },
    getOrientationProperties() {
      return {
        allowOrientationChange: true,
        forceOrientation: 'none'
      };
    },
    getMaxSize() {
      return getWindowSize();
    },
    getPlacementType() {
      return 'interstitial';
    },
    getResizeProperties() {
      return {
        allowOffscreen: false,
        customClosePosition: 'left',
        height: 0,
        offsetX: 0,
        offsetY: 0,
        width: 0
      };
    },
    getScreenSize() {
      return { width: screen.width, height: screen.height };
    },
    getState() {
      return 'default';
    },
    getVersion() {
      return '3.0';
    },
    isViewable() {
      if (
        options.viewableChangeStrategy ===
        VIEWABLE_CHANGE_STRATEGY.ALWAYS_VISIBLE
      ) {
        return true;
      }
      return isViewable();
    },
    isReady() {
      return true;
    },
    open(url) {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('target', '_blank');
      link.click();
    },
    playVideo() {},
    removeEventListener(event: MRAIDEvent | OguryEvent, cb: any) {
      if (listeners[event]) {
        listeners[event] = listeners[event].filter((x: any) => x !== cb);
      }
    },
    resize() {},
    setExpandProperties() {},
    setResizeProperties() {},
    setOrientationProperties() {},
    storePicture() {},
    supports(feature) {
      switch (feature) {
        case 'inlineVideo':
          return true;
        default:
          return false;
      }
    },
    useCustomClose() {},
    ogyCreateWebView(options: WebViewProperties, cb: OguryCallback) {},
    ogyUpdateWebView(
      webViewId: string,
      options: WebViewUpdateProperties,
      cb?: OguryCallback
    ) {},
    ogyCloseWebView(webViewId: string, cb: OguryCallback) {},
    ogyNavigateBack(webViewId: string) {},
    ogyNavigateForward(webViewId: string) {},
    ogyCreateShortcut(options: ShortcutProperties, cb: OguryCallback) {},
    ogyOnAdEvent(event: string) {},
    ogyResolveIntent(intentUri: string, cb: OguryCallback) {},
    ogyOnAdClicked() {},
    ogyOnAdImpression() {},
    ogyOnAdLoaded() {},
    ogyOpenStoreKit() {},
    ogyForceClose() {},
    ogyStartIntent() {},
    ogyCloseAndShowNextAd() {},
    ogyVideoAutoplayError() {},
    ogySendMonitoringLogs() {},
    ogyOpenSKOverlay() {},
    ogyCloseSKOverlay() {},
    ...getOCSMraidSpecificPayload()
  } as Mraid;
}

export function getOCSMraidSpecificPayload(): {
  ogySendTrackingEvent: () => void;
} {
  return {
    ogySendTrackingEvent: function() {
      const event = new CustomEvent('calltag', {
        detail: {
          method: 'ogySendTrackingEvent',
          args: {
            event: arguments[0],
            parameters: arguments[1]
          }
        }
      });
      window.dispatchEvent(event);
    }
  };
}

function unload() {
  cleanupFrame();
}

function cleanupFrame() {
  try {
    const selfFrame = window.frameElement as HTMLElement;
    if (selfFrame?.parentNode) {
      selfFrame.parentNode.removeChild(selfFrame);
    }
  } catch {
    // ignore cleanup errors
  }
}
